'use client';

import { CSSProperties, ReactNode, useRef, useState } from 'react';

import { css, cx } from 'styled-system/css';
import { button } from 'styled-system/recipes';

import { AnimateWhenInViewWrapper, Slider, SliderHandle } from '@/rsc/client';

// SliderControls (functional/SliderControls, variant light) without Chakra — as in ServicesRail.
const controlsClass = css({ display: { base: 'none', sm: 'flex' }, justifyContent: 'flex-end', alignItems: 'end' });
const arrowClass = css({ display: 'inline-flex', alignSelf: 'center', flexShrink: 0 });
const enabledClass = css({ opacity: 1, cursor: 'pointer' });
const disabledClass = css({ opacity: 0.3, cursor: 'not-allowed' });
// `gapPX={isAboveXS ? 24 : 20}` as CSS: the rail's `--slider-gap` reads this responsive var.
const railGapClass = css({ '--info-grid-slider-gap': { base: '20px', xs: '24px' } });
const RAIL_STYLE = { '--slider-gap': 'var(--info-grid-slider-gap)' } as CSSProperties;

const ArrowLeft = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <path
      d="M3.828 6.99968H16V8.99968H3.828L9.192 14.3637L7.778 15.7777L0 7.99968L7.778 0.22168L9.192 1.63568L3.828 6.99968Z"
      fill="#5F2DD3"
    />
  </svg>
);

const ArrowRight = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <path
      d="M12.172 6.99968L6.808 1.63568L8.222 0.22168L16 7.99968L8.222 15.7777L6.808 14.3637L12.172 8.99968H0V6.99968H12.172Z"
      fill="#5F2DD3"
    />
  </svg>
);

/**
 * Client island of the InfoGrid `dark_with_slider` mode (services/sections/slider SliderSection):
 * prev/next controls (≥ sm, CSS instead of `<Show above="sm">`) wired to the shared Slider; the
 * cards are server children. `useMediaQuery(above xs)` → `snapDirectionFromXs` + a CSS gap var.
 */
export const InfoGridSliderRail = ({ children }: { children: ReactNode }) => {
  const sliderRef = useRef<SliderHandle>(null);
  const [isAtStart, setIsAtStart] = useState(true);
  const [isAtEnd, setIsAtEnd] = useState(false);
  const buttonClass = button({ variant: 'sliderLight' });

  return (
    <>
      <div className={controlsClass}>
        <button
          type="button"
          aria-label="previous slide"
          onClick={() => sliderRef.current?.onPrev()}
          disabled={isAtStart}
          className={cx(buttonClass, isAtStart ? disabledClass : enabledClass)}
        >
          <span className={arrowClass}>
            <ArrowLeft />
          </span>
        </button>
        <button
          type="button"
          aria-label="next slide"
          onClick={() => sliderRef.current?.onNext()}
          disabled={isAtEnd}
          className={cx(buttonClass, css({ ml: 4 }), isAtEnd ? disabledClass : enabledClass)}
        >
          <span className={arrowClass}>
            <ArrowRight />
          </span>
        </button>
      </div>
      <div className={css({ pt: 8 })}>
        <AnimateWhenInViewWrapper>
          <Slider
            ref={sliderRef}
            snapDirection="center"
            snapDirectionFromXs="start"
            fullBleed
            className={railGapClass}
            style={RAIL_STYLE}
            onScrollChange={(state) => {
              setIsAtStart(state.isAtStart);
              setIsAtEnd(state.isAtEnd);
            }}
          >
            {children}
          </Slider>
        </AnimateWhenInViewWrapper>
      </div>
    </>
  );
};
